import {
  AppstoreOutlined,
  BookOutlined,
  FileTextOutlined,
  HomeOutlined,
  MessageOutlined,
  NodeExpandOutlined,
  PartitionOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SearchOutlined,
  ShareAltOutlined,
  SplitCellsOutlined,
  TeamOutlined,
  ToolOutlined
} from "@ant-design/icons";
import { withKeycloak } from "@react-keycloak/web";
import React from "react";
import { withRouter } from "react-router-dom";
import {
  checkSessionStatus,
  sessionAuthenticated
} from "../../../../keycloak/keycloakFunctions";
import { labels } from "../../../../localization/labels";
import CommonUtil from "../../../../utils/CommonUtil";
import { constants } from "../../../../utils/Constants";
import NotificationUtils from "../../../../utils/NotificationUtils";
import PathUtil from "../../../../utils/PathUtil";
import SecurityUtil from "../../../../utils/SecurityUtils";
import WebSocketClient from "../../../core/WebSocketClient";
import { paths } from "../../../paths";
import { knowledge_item_ASPS } from "../AdvancedSearchComponent/advanced_search_parameters";
import PageComponent from "../PageComponent";
import Page from "./Page";

const HttpStatus = require("http-status-codes");

class PageContainer extends PageComponent {
  constructor(props) {
    super(props);

    this.messageAlert = React.createRef();
    this.breadcrumbs = React.createRef();
    this.partyMessagesRef = React.createRef();

    this.state = {
      sideMenuCollapsed: false,
      sideMenuItems: []
    };

    this.breadcrumbTasks = [];
  }

  notificationBadgeRefreshPropagator = notificationBadgeRefresh => {
    if (!this.notificationBadgeRefresh)
      this.notificationBadgeRefresh = notificationBadgeRefresh;
  };

  getComponentTitle = () => {
    return undefined;
  };

  notificationHandler = {
    refreshNotifications: () => {
      if (this.notificationBadgeRefresh) {
        this.notificationBadgeRefresh();
      }
      if (this.partyMessagesRef?.current) {
        this.partyMessagesRef.current.reload();
      }
    }
  };

  messageHandler = {
    _canUseAlert: () => {
      return (
        this.messageAlert?.current !== undefined &&
        this.messageAlert?.current !== null
      );
    },

    handleSuccess: message => {
      if (this.messageHandler._canUseAlert()) {
        this.messageAlert.current.showSuccessMessage(message);
      }
    },

    handleWarning: message => {
      if (this.messageHandler._canUseAlert()) {
        this.messageAlert.current.showWarningMessage(message);
      }
    },

    handleError: message => {
      if (message.status === HttpStatus.UNAUTHORIZED) {
        checkSessionStatus(this.keycloak);
      } else if (this.messageHandler._canUseAlert()) {
        this.messageAlert.current.showErrorMessage(message);
      }
    },

    unhandle: () => {
      if (this.messageHandler._canUseAlert()) {
        this.messageAlert.current.dismissAlert();
      }
    }
  };

  breadcrumbsHandler = {
    _canUseBreadcrumbs: () => {
      return (
        this.breadcrumbs?.current !== undefined &&
        this.breadcrumbs?.current !== null
      );
    },
    _pushTask: (action, path, name) => {
      const task = { action: action, path: path, name: name };

      const lastTask =
        this.breadcrumbTasks?.length > 0
          ? this.breadcrumbTasks[this.breadcrumbTasks.length - 1]
          : undefined;
      if (
        !lastTask ||
        !(lastTask.action === task.action && lastTask.path === task.path)
      ) {
        this.breadcrumbTasks.push(task);
      }
    },
    addBreadcrumb: (path, name) => {
      if (this.breadcrumbsHandler._canUseBreadcrumbs()) {
        this.breadcrumbs.current.push(path, name);
      } else {
        this.breadcrumbsHandler._pushTask("ADD", path, name);
      }
    },
    resetBreadcrumb: (path, name) => {
      if (this.breadcrumbsHandler._canUseBreadcrumbs()) {
        this.breadcrumbs.current.restart(path, name);
      } else {
        this.breadcrumbsHandler._pushTask("RESET", path, name);
      }
    },
    gotoBreadcrumb: (path, name) => {
      if (this.breadcrumbsHandler._canUseBreadcrumbs()) {
        this.breadcrumbs.current.pop(path, name);
      } else {
        this.breadcrumbsHandler._pushTask("GOTO", path, name);
      }
    },
    revertBreadcrumb: () => {
      if (this.breadcrumbsHandler._canUseBreadcrumbs()) {
        this.breadcrumbs.current.pop();
      } else {
        this.breadcrumbsHandler._pushTask("REVERT");
      }
    },
    refreshBreadcrumb: (path, name) => {
      if (this.breadcrumbsHandler._canUseBreadcrumbs()) {
        this.breadcrumbs.current.refresh(path, name);
      } else {
        this.breadcrumbsHandler._pushTask("REFRESH", path, name);
      }
    },
    getBreadcrumbPosition: path => {
      if (this.breadcrumbsHandler._canUseBreadcrumbs()) {
        return this.breadcrumbs.current.position(path);
      }
      return -1;
    }
  };

  updateBreadcrumb = () => {
    const tasks = this.breadcrumbTasks;
    if (tasks.length > 0) {
      for (var i = 0; i < tasks.length; i++) {
        const task = tasks[i];

        switch (task.action) {
          case "ADD":
            this.breadcrumbsHandler.addBreadcrumb(task.path, task.name);
            break;
          case "RESET":
            this.breadcrumbsHandler.resetBreadcrumb(task.path, task.name);
            break;
          case "GOTO":
            this.breadcrumbsHandler.gotoBreadcrumb(task.path, task.name);
            break;
          case "REVERT":
            this.breadcrumbsHandler.revertBreadcrumb();
            break;
          case "REFRESH":
            this.breadcrumbsHandler.refreshBreadcrumb(task.path, task.name);
            break;
          default:
            break;
        }
      }
      this.breadcrumbTasks = [];
    }
  };

  onBreadcrumbClick = (path, name) => {
    const breadcrumbPosition = this.breadcrumbsHandler.getBreadcrumbPosition(
      path
    );
    this.changePath(path, name, {}, breadcrumbPosition);
  };

  componentDidUpdate() {
    this.updateBreadcrumb();
  }

  onSideMenuToggle = () => {
    this.setState({ sideMenuCollapsed: !this.state.sideMenuCollapsed });
  };

  componentDidMount() {
    super.componentDidMount();
    this.constructMenuItems();
  }

  constructStaticMenuItems() {
    const menuItemArray = [
      {
        key: paths.HOME_PATH,
        label: labels.home,
        icon: <HomeOutlined />
      },
      {
        key: paths.ADVANCED_SEARCH,
        label: labels.advancedSearch,
        icon: <SearchOutlined />,
        extra: {
          advancedSearchParams: knowledge_item_ASPS
        }
      },
      {
        key: paths.KNOWLEDGE_MANAGMENT,
        label: labels.knowledgeManagement,
        icon: <BookOutlined />
      },

      {
        label: labels.administration,
        icon: <ToolOutlined />,
        content: [
          {
            icon: <SafetyCertificateOutlined />,
            label: labels.partiesAndSecurity,
            content: [
              {
                icon: <TeamOutlined />,
                key: PathUtil.adminPath(paths.INDIVIDUALS),
                label: labels.individuals,
                processes: [constants.OPERATION_SECURITY_ADMINISTRATION]
              },
              {
                icon: <TeamOutlined />,
                key: PathUtil.adminPath(paths.ORGANISATIONS),
                label: labels.organisations,
                processes: [constants.OPERATION_SECURITY_ADMINISTRATION]
              },
              {
                icon: <SafetyCertificateOutlined />,
                key: PathUtil.adminPath(paths.INVOLVEMENTROLETYPES),
                label: labels.involvementRoleTypes,
                processes: [constants.OPERATION_SECURITY_ADMINISTRATION]
              },
              {
                icon: <SafetyOutlined />,
                key: PathUtil.adminPath(paths.ROLES),
                label: labels.roleTypes,
                processes: [constants.OPERATION_SECURITY_ADMINISTRATION]
              }
            ]
          },
          {
            icon: <AppstoreOutlined />,
            label: labels.entities,
            content: [
              {
                icon: <NodeExpandOutlined />,
                key: PathUtil.adminPath(paths.LIFE_CYCLE),
                label: labels.lifeCycle,
                processes: [constants.OPERATION_ENTITY_ADMINISTRATION]
              },
              {
                icon: <SplitCellsOutlined />,
                key: PathUtil.adminPath(paths.CLASSIFICATIONS),
                label: labels.classification,
                processes: [constants.OPERATION_ENTITY_ADMINISTRATION]
              },
              {
                icon: <MessageOutlined />,
                key: PathUtil.adminPath(paths.MESSAGE_LOG_TYPE),
                label: labels.messageLogType,
                processes: [constants.OPERATION_ENTITY_ADMINISTRATION]
              },
              {
                icon: <FileTextOutlined />,
                key: PathUtil.adminPath(paths.EXTENSION),
                label: labels.extensions,
                processes: [constants.OPERATION_ENTITY_ADMINISTRATION]
              },
              {
                icon: <PartitionOutlined />,
                key: PathUtil.adminPath(paths.ENTITY_RELATIONSHIP_TYPES),
                label: labels.entityRelationshipTypes,
                processes: [constants.OPERATION_ENTITY_ADMINISTRATION]
              }
            ]
          },

          {
            icon: <ReadOutlined />,
            key: PathUtil.adminPath(paths.KNOWLEDGE),
            label: labels.knowledge,
            processes: [constants.OPERATION_KNOWLEDGE_ADMINISTRATION]
          },
          {
            icon: <ShareAltOutlined />,
            key: PathUtil.adminPath(paths.SYSTEM),
            label: labels.system,
            processes: [constants.OPERATION_REINDEX_ALL]
          }
        ]
      }
    ];

    this.filterMenuItemArray(menuItemArray);
    return menuItemArray;
  }

  filterMenuItemArray = menuItemArray => {
    if (menuItemArray?.length > 0) {
      for (let i = 0; i < menuItemArray.length; i++) {
        const element = menuItemArray[i];
        if (element.content) {
          this.filterMenuItemArray(element.content);
        }
        if (element.content?.length === 0) {
          CommonUtil.removeElementFromArray(menuItemArray, element);
          i--;
        }
        if (element.processes) {
          if (!SecurityUtil.checkPartyHasAnyOfProcesses(element.processes)) {
            CommonUtil.removeElementFromArray(menuItemArray, element);
            i--;
          }
        }
      }
    }
  };

  constructMenuItems() {
    const staticItems = this.constructStaticMenuItems();

    var menuItems = staticItems;
    this.setState({ sideMenuItems: menuItems });
  }

  onMenuItemClick = selected => {
    const path = selected.key;
    var from = this.props.location.pathname === path ? path : undefined;
    this.changePath(path, selected.label, {
      ...selected.extra,
      from: from
    });
  };

  onHomeMenuClick = () => {
    if (this.state.sideMenuItems?.length) {
      this.onMenuItemClick(this.state.sideMenuItems[0]);
    }
  };

  onNotificationClick = () => {
    this.changePath(paths.PARTY_MESSAGES);
  };

  onPartyMessageUpdated = (data, topic) => {
    this.notificationHandler.refreshNotifications();
    if (CommonUtil.urlEndsWithNumber(topic)) {
      NotificationUtils.showNotification(
        data.name,
        data.content,
        this.onNotificationClick
      );
    }
  };

  render() {
    return (
      <>
        <Page
          sideMenuItems={this.state.sideMenuItems}
          sideMenuCollapsed={this.state.sideMenuCollapsed}
          onMenuItemClick={this.onMenuItemClick}
          onHomeMenuClick={this.onHomeMenuClick}
          location={this.props.location}
          onSideMenuToggle={this.onSideMenuToggle}
          alertRef={this.messageAlert}
          breadcrumbsRef={this.breadcrumbs}
          onBreadcrumbClick={this.onBreadcrumbClick}
          partyMessagesRef={this.partyMessagesRef}
          notificationBadgeRefreshPropagator={
            this.notificationBadgeRefreshPropagator
          }
          {...this.handlerProps()}
        />
        {sessionAuthenticated(this.keycloak) ? (
          <WebSocketClient
            topics={[
              constants.WEBSOCKET_TOPICS.PARTY_MESSAGES,
              constants.WEBSOCKET_TOPICS.PARTY_MESSAGES_ID
            ]}
            onMessage={this.onPartyMessageUpdated}
          />
        ) : null}
      </>
    );
  }
}

export default withKeycloak(withRouter(PageContainer));
